export interface TutorImage {
  mime_type: 'image/png';
  image_base64: string;
}
export interface TutorUpload extends TutorImage {
  name: string;
}
export const MAX_TUTOR_IMAGES = 3;
export function tutorImageUrl(image: TutorImage) {
  return `data:${image.mime_type};base64,${image.image_base64}`;
}
// Re-encode locally to strip metadata and bound the provider payload. No upload occurs here.
export async function prepareTutorImage(file: File): Promise<TutorUpload> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type))
    throw new Error('Choose a JPG, PNG or WebP image.');
  if (!file.size || file.size > 8 * 1024 * 1024) throw new Error('Choose an image under 8 MB.');
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('This image cannot be read. Choose another photo.'));
      image.src = url;
    });
    if (
      !image.naturalWidth ||
      !image.naturalHeight ||
      image.naturalWidth * image.naturalHeight > 24_000_000
    )
      throw new Error('Choose a photo under 24 megapixels.');
    const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Image preparation is unavailable. Try another browser.');
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const data = canvas.toDataURL('image/png');
    const base64 = data.split(',')[1];
    if (
      !data.startsWith('data:image/png;base64,') ||
      !base64 ||
      (base64.length * 3) / 4 - (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0) >
        2_000_000
    )
      throw new Error('This photo is too detailed. Choose a smaller area around the question.');
    return { name: file.name, mime_type: 'image/png', image_base64: base64 };
  } finally {
    URL.revokeObjectURL(url);
  }
}
